import { createContext, useEffect, useMemo, useState } from 'react';

import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import './index.css';

import { v4 as uuid } from 'uuid';
import { Acr } from './common/types/acr';
import { ClientData } from './common/types/client-data';
import './i18n';
import ClientDataContext from './store/client-data-context';
import muiTheme from './styles/theme';
import * as Config from 'Config';
import { PageHeader } from './components';
import { AuthFeature } from './features/auth/';
import SessionHttpContext from './store/session-http-context';
import { HttpStatus } from 'common/types/http-status';
import { useTranslation } from 'react-i18next';
import {
  CallbackSidHandled,
  CallbackEpsHandled,
  ForbiddenCountryError,
  GeneralError,
} from './features/auth/components';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

const themeBase = {
  logo: '',
  backgroundColor: '#FFFFFF',
};

export const ThemeContext = createContext(themeBase);
export const Base = () => {
  const [theme, setTheme] = useState(themeBase);
  const { i18n } = useTranslation();

  const [clientData, setClientData] = useState({} as unknown as ClientData);
  const [traceId, setTraceId] = useState('');
  const [sessionHttpContextValue, setSessionHttpContextValue] = useState<HttpStatus>({
    isLoading: true,
    httpResponse: null,
  });

  const clientDataContextValue = useMemo(() => {
    return { client: clientData, traceId: traceId };
  }, [clientData, traceId]);

  const getSupportedLocale = (apiLocales: string[] = []): string => {
    const supported = Array.isArray(i18n.options.supportedLngs) ? (i18n.options.supportedLngs as string[]) : [];

    if (!apiLocales || apiLocales.length === 0) return 'en';

    for (const raw of apiLocales) {
      const langPart = i18n.services.languageUtils.getLanguagePartFromCode(String(raw));
      const hit = supported.find((s) => s.toLowerCase() === langPart.toLowerCase());
      if (hit) return hit;
    }
    return 'en';
  };

  useEffect(() => {
    const newTraceID = uuid();
    setTraceId(newTraceID);

    fetch(Config.SESSION_DATA_URL, {
      credentials: 'include',
      headers: new Headers({
        'X-Trace-Id': newTraceID,
      }),
    })
      .then((data) => {
        setSessionHttpContextValue((state) => ({ ...state, httpResponse: data }));
        if (!data.ok) {
          throw new Error('Error getting client info');
        }
        return data.json();
      })
      .then((data: ClientData) => {
        setTheme({
          logo: data.logo,
          backgroundColor: data.backgroundColor,
        });

        const root: HTMLElement | null = document.querySelector(':root');

        if (data.backgroundColor) {
          root?.style.setProperty('--config-body-background-color', data.backgroundColor);
        }

        const supportedLocale = getSupportedLocale(data.locales ?? []);

        if (!data.locales || data.locales.length === 0) {
          i18n.changeLanguage('en');
        } else {
          i18n.changeLanguage(supportedLocale);
        }

        setClientData(data);
        filterCountryAndAuthMethod(data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setSessionHttpContextValue((state) => ({ ...state, isLoading: false }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function filterCountryAndAuthMethod(data: ClientData) {
    if (!data.allowedCountries?.length) {
      return;
    }

    if (data.acrValues?.length) {
      const orderedAcrCountries: string[] = Array.from(new Set(data.acrValues.flatMap((acr) => Acr.getCountries(acr))));
      setClientData((currentState) => ({
        ...currentState,
        allowedCountries: orderedAcrCountries.filter((acrCountry) => data.allowedCountries.includes(acrCountry)),
      }));
    }
  }

  return (
    <BrowserRouter basename="/login">
      <ThemeContext.Provider value={theme}>
        <ClientDataContext.Provider value={clientDataContextValue}>
          <SessionHttpContext.Provider value={sessionHttpContextValue}>
            <MuiThemeProvider theme={muiTheme}>
              <output className="screenreader-text"></output>
              <PageHeader />
              <Routes>
                <Route path="/callback-sid" element={<CallbackSidHandled />} />
                <Route path="/callback-eparaksts" element={<CallbackEpsHandled />} />
                <Route path="/forbidden-country" element={<ForbiddenCountryError />} />
                <Route path="/error" element={<GeneralError />} />
                <Route path="/" element={<AuthFeature />} />
              </Routes>
            </MuiThemeProvider>
          </SessionHttpContext.Provider>
        </ClientDataContext.Provider>
      </ThemeContext.Provider>
    </BrowserRouter>
  );
};
